import React, { useEffect } from 'react';
import FocusContext from '@mergermarket/acuris-react-focus-context';
import LoaderIndicator from '@mergermarket/acuris-react-loader-indicator';
import { TextSectionHeader } from '@mergermarket/acuris-react-section-header';
import { breakpoints, warmGrey, divisionColors, fontWeight, grid, fontFamily, colors } from '@mergermarket/acuris-react-styles';
import styled, { css } from 'styled-components';
import _debounce from 'lodash.debounce';
import Search from '@mergermarket/acuris-react-search';
import { formatIntelCount } from '@mergermarket/acuris-react-frontend-utils';
import { AcurisCompanyCard } from '@mergermarket/acuris-react-universal-card';
import { CrossIcon, SearchIcon } from '@mergermarket/acuris-react-icon';

const StyledSearchGroup = styled.div`
  .search-group-show-more {
    display: block;
    text-align: right;
    margin-top: 0.8rem;
  }
`;
const StyledSectionHeader = styled(TextSectionHeader)`
  margin: 0 0 0.8rem 0;
`;
const StyledSearchGroupList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  column-count: 0;

  ${({
  numberOfColumnsToDisplay
}) => numberOfColumnsToDisplay && css`
      @media (min-width: ${breakpoints.large}) {
        column-count: ${numberOfColumnsToDisplay};
        column-gap: 2.4rem;

        > li {
          break-inside: avoid;
        }
      }
    `}
`;
const StyledSearchGroupListItem = styled.li`
  &:not(:last-child) {
    .search-group-link,
    .search-group-link-place-holder {
      border-bottom: 0.1rem solid ${warmGrey.IV};
    }

    .company-card {
      margin-bottom: 0.8rem;
    }
  }

  &:last-child {
    margin-bottom: 0.6rem;
  }

  .search-group-link,
  .search-group-link-place-holder {
    display: block;
  }
`;
const StyledSearchGroupLink = styled.a`
  font-size: 1.4rem;
  padding: 0.8rem 0;
  color: ${divisionColors.darker};
`;
const StyledSearchGroupLinkPlaceHolder = styled.span`
  font-size: 1.4rem;
  padding: 0.8rem 0;
  color: ${warmGrey.VIII};
`;
const StyledSearchGroupShowMore = styled.a`
  font-size: 1.2rem;
  font-weight: ${fontWeight.bold};
  color: ${divisionColors.darker};
`;
const StyledGroupList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;
const StyledSearchResultsContainer = styled.div`
  margin: 0;
  padding: ${grid.doubleGutters.xsmall};

  && {
    .responsive-search-bar {
      display: block;
    }
  }

  @media screen and (min-width: ${breakpoints.medium}) {
    padding: ${grid.doubleGutters.medium};

    && {
      .responsive-search-bar {
        display: none;
      }
    }
  }

  @media screen and (min-width: ${breakpoints.large}) {
    padding: ${grid.doubleGutters.large};
  }

  @media screen and (min-width: ${breakpoints.xlarge}) {
    padding: ${grid.doubleGutters.xlarge};
  }
`;
const StyledSearchBarContainer = styled.span`
  .search {
    display: none;
  }

  @media screen and (min-width: ${breakpoints.medium}) {
    .search {
      display: block;
    }

    .search-button {
      display: none;
    }
  }
`;
const StyledTextContainer = styled.div`
  font-family: ${fontFamily.primary};
  margin-top: 1.7rem;
  margin-bottom: 5.5rem;

  @media screen and (min-width: ${breakpoints.small}) {
    margin-top: 2.6rem;
  }

  @media screen and (min-width: ${breakpoints.medium}) {
    margin-top: 3.2rem;
  }

  @media screen and (min-width: ${breakpoints.large}) {
    margin-top: 4.2rem;
    margin-left: 17.8rem;
  }

  @media screen and (min-width: ${breakpoints.xlarge}) {
    margin-left: 35.8rem;
  }

  @media screen and (min-width: ${breakpoints.xxlarge}) {
    margin-left: 40.5rem;
  }

  & + .search-results {
    padding-top: 0;
  }

  .text-container-body,
  .text-container-list-title,
  .text-container-list {
    font-family: ${fontFamily.primary};
    margin: 0;
  }

  .text-container-body {
    margin-bottom: 0.8rem;
  }

  .text-container-list-title {
    font-size: 1.4rem;
    margin-bottom: 0.6rem;
  }

  .text-container-list {
    list-style: disc;
    font-size: 1.4rem;
    padding: 0 0 0 1.6rem;
  }

  .text-container-list-item {
    list-style: disc;

    &:not(:last-child) {
      margin-bottom: 0.4rem;
    }
  }
`;
const StyledLoaderIndicator = styled(LoaderIndicator)`
  padding: 1.6rem;

  @media screen and (min-width: ${breakpoints.medium}) {
    padding: 0;
  }
`;

const NoResults = ({
  searchTerm,
  companyDirectoryUrl,
  id,
  role
}) => /*#__PURE__*/React.createElement(StyledTextContainer, {
  id: id,
  role: role
}, !companyDirectoryUrl && /*#__PURE__*/React.createElement("p", {
  className: "text-container-body"
}, "Sorry, we couldn't find any results matching ", /*#__PURE__*/React.createElement("strong", null, "\"", searchTerm, "\"")), companyDirectoryUrl && /*#__PURE__*/React.createElement("p", {
  className: "text-container-body"
}, "Sorry, we couldn't find any companies matching ", /*#__PURE__*/React.createElement("strong", null, "\"", searchTerm, "\"")), /*#__PURE__*/React.createElement("h2", {
  className: "primary-font text-container-list-title"
}, "Search tips:"), /*#__PURE__*/React.createElement("ul", {
  className: "text-container-list"
}, /*#__PURE__*/React.createElement("li", {
  className: "text-container-list-item"
}, "Check your spelling and try again"), !companyDirectoryUrl && /*#__PURE__*/React.createElement("li", {
  className: "text-container-list-item"
}, "Try broadening your query"), !companyDirectoryUrl && /*#__PURE__*/React.createElement("li", {
  className: "text-container-list-item"
}, "Try using different or fewer words"), companyDirectoryUrl && /*#__PURE__*/React.createElement("li", {
  className: "text-container-list-item"
}, "Use the", ' ', /*#__PURE__*/React.createElement("a", {
  href: companyDirectoryUrl
}, /*#__PURE__*/React.createElement("strong", null, "Company Directory")), ' ', "to find the company you are looking for")));

const canPerformSearch = searchTerm => (searchTerm || '').length > 1;
const hasGroups = (results = []) => results.length > 0;
const doGroupsHaveResults = (results = []) => results.some(group => group.count > 0);
const hasRespondedWithNoResults = (searchTerm, results) => canPerformSearch(searchTerm) && hasGroups(results) && !doGroupsHaveResults(results);
const SearchContext = React.createContext({
  setSearchTerm: _ => _,
  setLoading: _ => _,
  setResults: _ => _,
  setError: _ => _,
  search: () => new Promise(res => {
    res();
  })
});
const SearchContextConsumer = SearchContext.Consumer;
class SearchContextProvider extends React.Component {
  constructor(props) {
    super(props);

    this.search = async () => {
      const {
        searchTerm
      } = this.state;

      try {
        this.setLoading(true);

        if (canPerformSearch(searchTerm)) {
          const searchResults = await this.props.search(this.state.searchTerm);
          this.state.setError(undefined);
          this.state.setResults(searchResults);
        }
      } catch (e) {
        this.state.setError(e);
        this.state.setResults();
      } finally {
        this.setLoading(false);
      }
    };

    this.setSearchTerm = value => {
      this.setState({
        searchTerm: value
      }, this.search);
    };

    this.setResults = value => {
      this.setState({
        results: value
      });
    };

    this.setError = value => {
      this.setState({
        error: value
      });
    };

    this.setLoading = value => {
      this.setState({
        loading: value
      });
    };

    this.state = {
      setSearchTerm: this.setSearchTerm,
      setLoading: this.setLoading,
      setResults: this.setResults,
      setError: this.setError,
      search: this.search
    };
    this.search = _debounce(this.search, 420);
  }

  render() {
    return /*#__PURE__*/React.createElement(SearchContext.Provider, {
      value: this.state
    }, this.props.children);
  }

}

const SearchError = props => {
  const [seconds, setSeconds] = React.useState(props.retrySeconds);
  const canRetry = props.retryCount < props.maxRetries;
  React.useEffect(() => {
    let timerID;

    if (canRetry) {
      timerID = setTimeout(() => {
        if (seconds === 0) {
          props.onRetry && props.onRetry();
          setSeconds(props.retrySeconds);
        } else {
          setSeconds(seconds - 1);
        }
      }, 1000);
    }

    return () => clearTimeout(timerID);
  });
  return /*#__PURE__*/React.createElement(StyledTextContainer, {
    id: props.id,
    role: props.role
  }, /*#__PURE__*/React.createElement("p", {
    className: "text-container-body"
  }, "Sorry, something seems to have gone wrong!"), canRetry && /*#__PURE__*/React.createElement("h2", {
    "data-test-seconds": true,
    className: "primary-font text-container-list-title"
  }, "Retrying in... ", seconds > 0 && seconds) || /*#__PURE__*/React.createElement("h2", {
    className: "primary-font text-container-list-title"
  }, "Please try again later."));
};

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

const SearchInput = ({
  id,
  searchProps,
  onFocus,
  onChange,
  className = ''
}) => {
  const searchContext = React.useContext(SearchContext);
  return /*#__PURE__*/React.createElement(Search, _extends({
    placeholder: "Search",
    label: "Search"
  }, searchProps, {
    id: `${id}_header-input`,
    className: className,
    autoComplete: "off",
    searchValue: searchContext.searchTerm || '',
    ariaDescribedby: searchContext.error && 'searchError' || hasRespondedWithNoResults(searchContext.searchTerm, searchContext.results) && 'searchNoResults' || 'searchResults',
    onFocus: onFocus,
    onChange: e => {
      const input = e.target;
      searchContext.setSearchTerm(input.value);
      onChange && onChange(e);
    },
    onClear: () => searchContext.setSearchTerm(undefined)
  }));
};

let ESearchGroupType;

(function (ESearchGroupType) {
  ESearchGroupType["COMPANY"] = "company";
  ESearchGroupType["LINK"] = "link";
})(ESearchGroupType || (ESearchGroupType = {}));

function _extends$1() { _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }
const SearchGroup = ({
  data
}) => {
  const linkItems = data.type === ESearchGroupType.LINK && data.items;
  const companyItems = data.type === ESearchGroupType.COMPANY && data.items;
  return /*#__PURE__*/React.createElement(StyledSearchGroup, {
    className: `${data.type}-search-group`,
    "data-test-search-group": true
  }, /*#__PURE__*/React.createElement(StyledSectionHeader, {
    text: data.title,
    count: data.count,
    shouldShowZeros: true,
    isInDropdown: true
  }), /*#__PURE__*/React.createElement(StyledSearchGroupList, {
    numberOfColumnsToDisplay: data.numberOfColumnsToDisplay
  }, linkItems && linkItems.map(item => /*#__PURE__*/React.createElement(StyledSearchGroupListItem, {
    key: item.href
  }, item.count === 0 ? /*#__PURE__*/React.createElement(StyledSearchGroupLinkPlaceHolder, {
    className: "search-group-link-place-holder"
  }, item.text, typeof item.count !== 'undefined' && /*#__PURE__*/React.createElement("span", null, " (", formatIntelCount(item.count), ")")) : /*#__PURE__*/React.createElement(StyledSearchGroupLink, {
    className: "search-group-link",
    href: item.href
  }, item.text, typeof item.count !== 'undefined' && /*#__PURE__*/React.createElement("span", null, " (", formatIntelCount(item.count), ")")))), companyItems && companyItems.map(item => /*#__PURE__*/React.createElement(StyledSearchGroupListItem, {
    key: item.companyID
  }, /*#__PURE__*/React.createElement(AcurisCompanyCard, _extends$1({
    className: "company-card"
  }, item))))), data.showMoreLink && data.showMoreLink.count > 0 && /*#__PURE__*/React.createElement(StyledSearchGroupShowMore, {
    className: "search-group-show-more",
    href: data.showMoreLink.href
  }, `View all ${formatIntelCount(data.showMoreLink.count)} ${data.title.toLowerCase()}`));
};

const NO_PADDING_WIDTH = '100%';

const convertToBreakpointPxKeys = gutterPx => Object.entries(breakpoints).reduce((acc, [key, value]) => ({ ...acc,
  [`${value}`]: gutterPx[key]
}), {});

const gutters = convertToBreakpointPxKeys(grid.gutters);
const doubleGutters = convertToBreakpointPxKeys(grid.doubleGutters);
const searchGroupWidths = {
  [breakpoints.medium]: [['100%'], ['50%', '50%'], ['100%', '100%', '100%'], ['50%', '50%', '100%', '100%'], ['100%', '100%', '100%', '100%', '100%']],
  [breakpoints.large]: [['100%'], ['75%', '25%'], ['33%', '33%', '33%'], ['25%', '25%', '25%', '25%'], ['25%', '25%', '25%', '25%', '100%']],
  [breakpoints.xlarge]: [['100%'], ['75%', '25%'], ['50%', '25%', '25%'], ['25%', '25%', '25%', '25%'], ['25%', '25%', '25%', '25%', '100%']]
};

const getColumnsCount = ({
  cards,
  links
}) => cards.length + Number(links.length > 0);

const applyColumnWidthsForBreakpoint = breakpoint => () => props => {
  const columnCount = getColumnsCount(props);
  const widthMatrix = searchGroupWidths[breakpoint][columnCount - 1];
  let groupsCss = ``;

  for (let i = 0; i < columnCount; i += 1) {
    groupsCss += `
      .search-group-list-item:nth-child(${i + 1}) {
        width: ${widthMatrix[i]};
        ${widthMatrix[i] === NO_PADDING_WIDTH ? css`
                padding: 0;
                margin-top: ${doubleGutters[breakpoint]};
              ` : css`
                margin-top: 0;

                &:not(:first-child) {
                  padding-left: ${gutters[breakpoint]};
                }

                &:not(:last-child) {
                  padding-right: ${gutters[breakpoint]};
                }
              `}
      }
    `;
  }

  return css`
    @media screen and (min-width: ${breakpoint}) {
      ${groupsCss}
    }
  `;
};

const StyledSearchResults = styled.ul`
  list-style: none;
  padding: ${gutters[breakpoints.small]} 0;
  margin: 0;
  width: 100%;

  @media screen and (min-width: ${breakpoints.xsmall}) {
    .search-group-list-item:not(:first-child) {
      margin-top: ${doubleGutters[breakpoints.medium]};
      margin-bottom: ${doubleGutters[breakpoints.medium]};
    }
    
    .search-group-list-item:last-child {
      margin-bottom: 2.6rem;
    }
  }

  @media screen and (min-width: ${breakpoints.medium}) {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    align-items: start;
    justify-content: space-between;
    padding: 1.6rem 0;
  }

  ${applyColumnWidthsForBreakpoint(breakpoints.medium)}
  ${applyColumnWidthsForBreakpoint(breakpoints.large)}
  ${applyColumnWidthsForBreakpoint(breakpoints.xlarge)}
`;

function getSortedResultsByGroup(results = []) {
  const links = [];
  const cards = [];
  results.forEach(data => {
    if (data.type === ESearchGroupType.COMPANY) {
      cards.push(data);
    } else {
      links.push(data);
    }
  });
  return {
    cards,
    links
  };
}

const SearchResults = props => {
  const {
    results
  } = React.useContext(SearchContext);
  const {
    cards,
    links
  } = getSortedResultsByGroup(results);
  return /*#__PURE__*/React.createElement(StyledSearchResults, {
    className: "search-results",
    cards: cards,
    links: links,
    id: props.id,
    role: props.role
  }, cards.map(data => /*#__PURE__*/React.createElement("li", {
    key: data.title,
    className: "search-group-list-item company-search-group-list-item"
  }, /*#__PURE__*/React.createElement(SearchGroup, {
    data: data
  }))), links.length > 0 && /*#__PURE__*/React.createElement("li", {
    className: "search-group-list-item link-search-group-list-item"
  }, /*#__PURE__*/React.createElement(StyledGroupList, null, links.map(data => /*#__PURE__*/React.createElement("li", {
    key: data.title
  }, /*#__PURE__*/React.createElement(SearchGroup, {
    data: data
  }))))));
};

const ResponsiveSearchResults = ({
  searchProps,
  id,
  onFocus
}) => {
  const searchContext = React.useContext(SearchContext);
  const focusContext = React.useContext(FocusContext);
  const [retryCount, setRetryCount] = React.useState(0);
  React.useEffect(() => {
    focusContext.setContentAttributes({
      id: 'search-results-container',
      'aria-label': `Your search results for ${searchContext.searchTerm}`,
      'aria-live': 'polite'
    });
  }, [searchContext.searchTerm]);
  return /*#__PURE__*/React.createElement(StyledSearchResultsContainer, null, /*#__PURE__*/React.createElement(SearchInput, {
    id: id,
    className: "responsive-search-bar",
    searchProps: searchProps,
    onFocus: onFocus
  }), searchContext.loading && /*#__PURE__*/React.createElement(StyledLoaderIndicator, {
    isVisible: true,
    text: "Loading results",
    size: "small"
  }), searchContext.error && /*#__PURE__*/React.createElement(SearchError, {
    id: "searchError",
    role: "alert",
    retryCount: retryCount,
    maxRetries: 3,
    retrySeconds: 5,
    onRetry: () => {
      setRetryCount(retryCount + 1);
      searchContext.search();
    }
  }), hasRespondedWithNoResults(searchContext.searchTerm, searchContext.results) && /*#__PURE__*/React.createElement(NoResults, {
    id: "searchNoResults",
    role: "alert",
    searchTerm: searchContext.searchTerm || ''
  }), canPerformSearch(searchContext.searchTerm) && hasGroups(searchContext.results) && /*#__PURE__*/React.createElement(SearchResults, {
    id: "searchResults",
    role: "alert"
  }));
};

const StyledCloseIcon = styled(CrossIcon)`
  transform: rotate(45deg);
  display: inline-block;
`;

// this is copied from acuris-react-user-menu/styledComponents/UserMenuButton.tsx
const StyledHeaderButton = styled.button`
  border-radius: 0.3rem;
  margin: 0;
  width: 2.6rem;
  height: 2.6rem;
  min-height: 2.6rem;
  min-width: 2.6rem;
  padding: 0;
  background: ${warmGrey.I};
  border: 0;
  transition: none;
  outline: 0;
  position: relative;
  z-index: 3;

  ${props => props.isSelected ? `border: 0.1rem solid ${warmGrey.IV};` : `border: 0.1rem solid transparent;`};

  &:focus,
  &:visited,
  &:active {
    background: ${warmGrey.IV};
  }

  &:hover .icon {
    fill: ${colors.primaryBrandHover};
  }

  &:active {
    border: 0.1rem solid ${warmGrey.IV};
    box-shadow: inset 0 0.2rem 0.3rem 0 ${warmGrey.VII};
    .icon {
      fill: ${colors.primaryBrand};
    }
  }

  @media screen and (min-width: ${breakpoints.xsmall}) {
    width: 3.2rem;
    height: 3.2rem;
  }

  @media screen and (min-width: ${breakpoints.large}) {
    width: 3.6rem;
    height: 3.6rem;
  }

  .icon {
    position: static;
    margin: 0;
    width: 1.6rem;
    height: 1.6rem;
    ${props => props.isSelected ? `fill: ${colors.primaryBrand};` : `fill: ${warmGrey.VII};`};
  }
`;

const SearchButton = ({
  id,
  onClick
}) => {
  const {
    activeMenu
  } = React.useContext(FocusContext);
  const isSelected = activeMenu === 'search';
  return /*#__PURE__*/React.createElement(StyledHeaderButton, {
    className: "search-button",
    id: id,
    onClick: e => {
      e.stopPropagation();
      return onClick && onClick(isSelected);
    },
    isSelected: isSelected
  }, isSelected ? /*#__PURE__*/React.createElement(StyledCloseIcon, {
    title: "Close search"
  }) : /*#__PURE__*/React.createElement(SearchIcon, {
    title: "Search"
  }), /*#__PURE__*/React.createElement("span", {
    className: "visuallyhidden"
  }, "Search"));
};

const SearchBar = props => {
  const searchContext = React.useContext(SearchContext);
  const focusContext = React.useContext(FocusContext);
  const container = React.createRef();
  const {
    id,
    className,
    searchProps: searchProps
  } = props;
  useEffect(() => {
    focusContext.setElement(container.current);
  }, [searchContext.loading]);
  useEffect(() => {
    onSearchChange(searchContext.searchTerm);
  }, [searchContext.searchTerm]);
  useEffect(() => {
    searchContext.results && focusContext.setContent(getContent());
  }, [searchContext.results]);

  const getContent = () => /*#__PURE__*/React.createElement(ResponsiveSearchResults, {
    searchProps: searchProps,
    id: `${id}_results-input`,
    onFocus: onFocus
  });

  const onSearchChange = searchTerm => {
    if (canPerformSearch(searchTerm)) {
      searchContext.setLoading(true);
    }

    focusContext.setElement(container.current);
    focusContext.setHasTabbedContent(canPerformSearch(searchTerm) && hasGroups(searchContext.results) || hasRespondedWithNoResults(searchTerm, searchContext.results));

    if (canPerformSearch(searchTerm)) {
      focusContext.setContent(getContent());
      focusContext.setActiveMenu('search');
    } else {
      searchContext.setResults(undefined);
    }
  };

  const onFocus = e => {
    const input = e.target;

    if (canPerformSearch(input.value)) {
      focusContext.setElement(container.current);
      focusContext.setContent(getContent());
      focusContext.setActiveMenu('search');
    }

    focusContext.setHasTabbedContent(canPerformSearch(searchContext.searchTerm) && hasGroups(searchContext.results) || hasRespondedWithNoResults(searchContext.searchTerm, searchContext.results));
  };

  return /*#__PURE__*/React.createElement(StyledSearchBarContainer, {
    id: id,
    className: className,
    ref: container
  }, /*#__PURE__*/React.createElement(SearchButton, {
    id: `${id}_button`,
    onClick: isSelected => {
      focusContext.setElement(container.current);
      focusContext.setContent(!isSelected ? getContent() : null);
      focusContext.setActiveMenu(!isSelected ? 'search' : null);
    }
  }), /*#__PURE__*/React.createElement(SearchInput, {
    id: id,
    searchProps: searchProps,
    onFocus: onFocus,
    onChange: e => {
      if (!canPerformSearch(e.target.value)) {
        focusContext.setContent(null);
      }
    }
  }));
};

export default SearchBar;
export { ESearchGroupType, ResponsiveSearchResults, SearchBar, SearchContext, SearchContextConsumer, SearchContextProvider, SearchResults };
//# sourceMappingURL=index.js.map
